import { cn, getObjectKeys } from '@gettactic/helpers';
import { capitalize } from '@gettactic/helpers/src/strings/capitalize';
import React from 'react';
import Scrollbars, { positionValues } from 'react-custom-scrollbars';
import { useIconDictionaries } from '../../icons/LucideIcon/useIconDictionaries';
import { Tabs, TabsList, TabsTrigger } from '../../primitives';
import { IconPickerContext, IconPickerProvider } from './IconPicker.provider';
import {
  IconDictionaries,
  IconDictionaryItem,
  IconPickerProps
} from './IconPicker.types';
import { IconPickerIconButton } from './IconPickerIconButton';

const classes = {
  IconPickerWrapper: 'h-[320px]',
  IconPickerLoadingWrapper: 'items-center justify-center',
  IconPickerGrid: 'gap-2 pl-2 py-4 grid-cols-[1fr_1fr_1fr_1fr_1fr]',
  IconPickerSearchInputWrapper: 'w-full',
  Tabs: 'w-full h-full flex flex-col',
  TabsList: 'py-2 rounded-none border-b bg-muted',
  IconPickerSearchInput:
    'border-none border-b shadow-none bg-secondary rounded-none'
};

export const IconPicker: React.FC<IconPickerProps> = (props) => {
  const iconDictionaries = useIconDictionaries();

  if (!iconDictionaries) {
    return (
      <div
        className={cn(
          'flex',
          classes.IconPickerWrapper,
          classes.IconPickerLoadingWrapper
        )}
      >
        {}
        Loading...
      </div>
    );
  }

  return <IconPickerInner iconDictionaries={iconDictionaries} {...props} />;
};

export const IconPickerInner: React.FC<
  IconPickerProps & { iconDictionaries: IconDictionaries }
> = ({ onSelect, initialIconSet, iconDictionaries, customSets }) => {
  const scrollBarsRef = React.useRef<Scrollbars | null>(null);
  const handleIconSelect = (iconName: string, iconSet: string) => {
    onSelect?.({ icon: iconName, set: iconSet });
  };

  const handleSearchValueChange = () => {
    scrollBarsRef.current?.scrollTop(0);
  };

  const iconPickerDictionaries: Record<string, IconDictionaryItem> =
    React.useMemo(() => {
      //const customIconsKeys = getObjectKeys({});
      return {
        ...iconDictionaries,
        custom: {
          icons: [] as never,
          keys: [] as never
        },
        ...customSets
      };
    }, []);

  return (
    <IconPickerProvider
      dictionaries={iconPickerDictionaries}
      onSearchValueChange={handleSearchValueChange}
      initialIconSet={initialIconSet}
    >
      <IconPickerContext.Consumer>
        {(state) => {
          if (!state) return null;

          const handleScrollChange = (values: positionValues) => {
            if (values.top >= 0.9 && state.page + 1 <= state.totalIconsPages) {
              state.setPage((page) => page + 1);
            }
          };

          return (
            <>
              <Tabs
                defaultValue="custom"
                value={state.iconSet}
                className={classes.Tabs}
                onValueChange={(value) => state.setIconSet(value as never)}
              >
                <TabsList className={classes.TabsList}>
                  {customSets &&
                    getObjectKeys(customSets).map((key) => (
                      <TabsTrigger key={key} value={key}>
                        {capitalize(key)}
                      </TabsTrigger>
                    ))}
                  <TabsTrigger value="mdi">Material Icons</TabsTrigger>
                  <TabsTrigger value="lucide">Lucide Icons</TabsTrigger>
                </TabsList>
              </Tabs>
              <input
                className={classes.IconPickerSearchInputWrapper}
                onChange={(e) => {
                  state.setSearchValue(e.target.value);
                }}
                placeholder={'Search'}
              />
              <Scrollbars
                ref={scrollBarsRef}
                className={classes.IconPickerWrapper}
                onUpdate={handleScrollChange}
                style={{ height: undefined }}
              >
                <div className={cn('grid', classes.IconPickerGrid)}>
                  {state.availableIcons
                    ? state.availableIcons.map((iconName) => {
                        return (
                          <div key={iconName}>
                            <IconPickerIconButton
                              iconName={
                                iconPickerDictionaries[state.iconSet].icons[
                                  iconName
                                ]
                              }
                              onClick={handleIconSelect.bind(
                                null,
                                iconName,
                                state.iconSet
                              )}
                              icon={iconName}
                              iconSet={state.iconSet}
                            />
                          </div>
                        );
                      })
                    : undefined}
                </div>
              </Scrollbars>
            </>
          );
        }}
      </IconPickerContext.Consumer>
    </IconPickerProvider>
  );
};
